/* ═══════════════════════════════════════════════════════════════════════════
   CASEMIRA C8 · vague 2, action 2.3 · CHARGEMENT DIFFÉRÉ DE L'ASSISTANT « M »
   ---------------------------------------------------------------------------
   Décision D4 de Raph du 04/09 : oui à une façade pour le chat. Voici ce qui a
   été fait, et surtout ce qui ne l'a pas été, avec la raison.

   ── LE FAIT MESURÉ ────────────────────────────────────────────────────────
   Le bloc `custom_liquid` « Assistant de conversation » de
   `sections/footer-group.json` posait, sur TOUTES les pages :

     <script src="https://casemira-chat.pages.dev/widget/casemira-chat.js" defer>

   Son commentaire affirmait que le script « ne déclenche AUCUN appel réseau
   tant que le visiteur n'a pas ouvert la bulle ». C'est faux au chargement
   (AUDIT § b.2 iv) : 61,9 Ko transférés, 193 Ko décodés, 61 % exécutés, plus
   deux avatars de 26,5 et 25,7 Ko. Sur la page 404, ces deux avatars étaient
   les deux plus grosses images de la page.

   Et surtout, `defer` ne veut pas dire « gratuit » : un script différé est
   TÉLÉCHARGÉ pendant l'analyse du document, en concurrence avec l'image LCP
   sur une 4G lente, puis ANALYSÉ ET COMPILÉ avant `DOMContentLoaded`,
   c'est-à-dire en plein dans la fenêtre que Lighthouse compte en TBT. Or le
   poste dominant du fil principal de ce site n'est aucun script en
   particulier : c'est l'analyse et la compilation du moteur JS, 56 à 58 % du
   temps (AUDIT § b.19). Le seul levier est le volume d'octets à analyser dans
   cette fenêtre. 193 Ko en sortent ici.

   ── LE GESTE ──────────────────────────────────────────────────────────────
   Le `<script>` tiers quitte le document. Ce fichier (moins de 2 Ko, même
   origine, mis en cache un an) l'injecte lui-même :
     · au premier signal d'intention APRÈS `load` (pointerdown, touchstart,
       keydown) : le visiteur qui touche la page cherche peut-être la bulle ;
     · sinon, en `requestIdleCallback` 2,5 s après `load`.
   Le premier des deux gagne, et il n'y a jamais deux injections.

   ── CE QUI CHANGE POUR LE VISITEUR, ET RIEN D'AUTRE ───────────────────────
   Le widget se monte déjà lui-même après `load` puis au premier temps creux
   (sa fonction `auCalme`), révèle son médaillon 1 500 ms plus tard, et déplie
   sa carte d'invitation à 5 000 ms (`DELAI_ARRIVEE`, relevé dans le fichier du
   widget). Tout cela est conservé À L'IDENTIQUE, simplement décalé du délai
   d'attente ci-dessus plus le temps de téléchargement des 62 Ko : le médaillon
   et la carte arrivent environ 2,5 à 3,5 s plus tard qu'avant. Aucune autre
   différence : mêmes dimensions, même position, même ombre, même animation,
   même comportement au tiroir, même barre d'achat collante — puisque c'est le
   VRAI widget qui les dessine, et non une copie.

   ── POURQUOI PAS UNE BULLE PEINTE EN CSS, qui était la lettre du brief ─────
   Parce qu'elle devrait dupliquer cinq comportements d'un widget maintenu dans
   un dépôt EXTÉRIEUR (mes-valises/casemira-chat), et se désynchroniserait au
   premier changement là-bas :
     1. le médaillon lui-même : 60 px, `.av` de 42 px en dégradé radial, filet
        `--liser`, badge d'encre de 20 px avec son icône, et un monogramme
        « M » composé dans Playfair Display que le widget télécharge en
        sous-ensemble d'un seul glyphe (`text=M`) au montage — une bulle peinte
        sans cette police afficherait un M en Georgia, puis le vrai M sauterait ;
     2. l'instant d'apparition : 1 500 ms après le montage, MAIS sur l'accueil
        au téléphone seulement une fois le bandeau de réassurance entré dans
        l'écran (`apresLeHeros`, avec repli à un demi-écran de défilement) ;
     3. la synchronisation `--lift` avec la barre d'achat collante de la fiche ;
     4. l'effacement quand un tiroir s'ouvre (menu, panier, pop-up newsletter) ;
     5. le croisement avec le vrai médaillon au moment de la relève.
   Le gain d'une bulle peinte se limite au temps de téléchargement (≈ 1 s), et
   son coût est un risque permanent de divergence au pixel dans le coin le plus
   visible de chaque page. La contrainte de Raph étant « zéro changement visuel,
   zéro bug », l'arbitrage est écrit dans
   `chantier8-vitesse/VAGUE-2.md § 2.3` : à lui de trancher s'il veut la
   seconde seconde.

   ── CE QUI N'EST PAS TOUCHÉ ───────────────────────────────────────────────
   `window.CASEMIRA_CHAT_API` et `window.CASEMIRA_CHAT_CLIENT` restent écrits
   en ligne dans le bloc, avant ce script : le widget les lit au montage comme
   avant. L'URL du widget est d'ailleurs DÉRIVÉE de `CASEMIRA_CHAT_API`, pour
   qu'il n'y ait qu'un seul endroit à changer le jour où l'origine change. La
   politique `cm-over` de `casemira-tokens.css` cible `#cmchat .lanceur` : le
   widget crée toujours ce nœud, elle continue de s'appliquer sans un mot de
   changé. Le dépôt du widget n'est pas modifié.

   ── RETOUR ARRIÈRE ────────────────────────────────────────────────────────
   Dans `sections/footer-group.json`, remettre la ligne
   `<script src="https://casemira-chat.pages.dev/widget/casemira-chat.js"
   defer></script>` à la place du `<script>` qui appelle ce fichier, et
   supprimer ce fichier. Rien d'autre n'en dépend.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* Le widget se protège déjà d'un double montage avec `window.__casemiraChat`,
     mais on ne compte pas là-dessus : deux <script> identiques, ce sont deux
     téléchargements et deux analyses, c'est-à-dire exactement ce qu'on retire. */
  if (window.__casemiraChatFacade) return;
  window.__casemiraChatFacade = true;

  var base = window.CASEMIRA_CHAT_API;
  if (!base) return; /* pas d'origine déclarée : rien à charger, rien à casser */

  var DELAI_APRES_LOAD = 2500;
  var pose = false;

  function poser() {
    if (pose) return;
    pose = true;
    detacher();
    var s = document.createElement('script');
    s.src = base.replace(/\/+$/, '') + '/widget/casemira-chat.js';
    /* `async`, pas `defer` : le document est déjà analysé quand on arrive ici,
       les deux se valent, et `async` dit mieux ce qui se passe. */
    s.async = true;
    document.head.appendChild(s);
  }

  /* --- L'intention, mais seulement après `load` ---------------------------
     Avant `load`, un geste du visiteur ne peut pas viser la bulle : elle
     n'existe pas encore, même dans la version d'avant ce fichier (le widget ne
     la révèle qu'après `load` + 1 500 ms). Charger 193 Ko à ce moment-là
     reviendrait à les remettre dans la fenêtre qu'on vient de leur faire
     quitter. Les écouteurs ne sont donc posés qu'une fois la page chargée. */
  var gestes = ['pointerdown', 'touchstart', 'keydown'];
  function surGeste() {
    poser();
  }
  function detacher() {
    for (var i = 0; i < gestes.length; i++) {
      document.removeEventListener(gestes[i], surGeste, true);
    }
  }
  function attacher() {
    for (var i = 0; i < gestes.length; i++) {
      document.addEventListener(gestes[i], surGeste, { capture: true, passive: true });
    }
  }

  function apresLoad() {
    attacher();
    window.setTimeout(function () {
      if (window.requestIdleCallback) window.requestIdleCallback(poser, { timeout: 4000 });
      else poser();
    }, DELAI_APRES_LOAD);
  }

  if (document.readyState === 'complete') apresLoad();
  else window.addEventListener('load', apresLoad, { once: true });
})();
